import client from '@config/client'
import API_URL from '@config/services'
import { ApiClientResult } from '@/types/api'
import queryString from '@utils/queryString'

interface ProjectPayload {
  name?: string
  description?: string
  status?: string
  date_start?: string
  date_end?: string
  [key: string]: any
}

interface ProjectUserPayload {
  project: number | string
  user: number | string
  role?: string
  [key: string]: any
}

interface ProjectTagPayload {
  name: string
  color?: string
  group?: number | string
  [key: string]: any
}

interface TagGroupPayload {
  name: string
  color?: string
  [key: string]: any
}

interface ProjectBulkPayload {
  projects: (string | number)[]
  users: (string | number)[]
  [key: string]: any
}

interface ProjectActionPayload {
  action: string
  project_id: number | string
  [key: string]: any
}

/**
 * Projects
 **/

/**
 * Marks a project as favorite
 * @param projectPk - Project ID
 * @param payload - Favorite data
 * @returns API response
 */
export const postFavoriteProject = (
  projectPk: number | string,
  payload: { is_favorite: boolean }
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.PROJECTS.POST_PROJECTS_FAVORITE(projectPk), payload)
    .then(response => response)
    .catch(error => error.response)
}

/* Top Bar */
/**
 * Constructs URL for opportunities top bar
 * @param organization - Organization ID
 * @param organizationTeam - Team ID
 * @returns URL string
 */
export const opportunitiesTopBarURL = (
  organization: string | number,
  organizationTeam: string | number
): string =>
  API_URL.PROJECTS.GET_PROJECT_LIST_PK +
  '?' +
  queryString({
    status: 'open',
    page_size: 9999999,
    team: organizationTeam
  })

/**
 * Constructs URL for projects top bar
 * @returns URL string
 */
export const projectsTopBarURLString: string =
  API_URL.PROJECTS.GET_PROJECT_LIST_PK +
  '?' +
  queryString({
    is_archived: false,
    signed: true,
    // status: ['signed', 'hold', 'completed', 'paid', 'litigious'],
    all_projects: true,
    page_size: 9999
  })

/**
 * URL for projects top bar - for use with useSWR
 */
export const projectsTopBarURL = projectsTopBarURLString
/* End Top Bar */

/**
 * Fetches signed projects
 * @returns Projects list
 */
export const fetchProjects = (): Promise<any[]> => {
  return client
    .get(API_URL.PROJECTS.GET_PROJECT_LIST_SIGNED)
    .then(response => response.data.results)
    .catch(error => error.response)
}

/**
 * Creates a new project
 * @param payload - Project data
 * @returns API response
 */
export const createProjects = (
  payload: ProjectPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.PROJECTS.POST_PROJECT_LIST_PK, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates project users
 * @param id - Project user ID
 * @param payload - Project user data
 * @returns API response
 */
export const patchProjectsUsers = (
  id: number | string,
  payload: Partial<ProjectUserPayload>
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.PROJECTS.PATCH_PROJECT_USERS_PK(id), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Deletes a project user
 * @param id - Project user ID
 * @returns API response
 */
export const deleteProjectsUsers = (
  id: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.PROJECTS.DELETE_PROJECT_USERS_PK(id))
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Deletes a project
 * @param id - Project ID
 * @returns API response
 */
export const deleteProject = (
  id: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.PROJECTS.DELETE_PROJECT(id))
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates a project
 * @param id - Project ID
 * @param payload - Project data
 * @param headers - Optional request headers
 * @returns API response
 */
export const patchProjects = (
  id: number | string,
  payload: Partial<ProjectPayload>,
  headers: Record<string, string> = {}
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.PROJECTS.PATCH_PROJECT_PK(id), payload, headers)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Resets invoicing planning for a project
 * @param id - Project ID
 * @returns API response
 */
export const resetInvoicingPlanningPk = (
  id: number | string
): Promise<ApiClientResult> => {
  return client
    .post(
      API_URL.PROJECTS.POST_PROJECT_ACTIONS_PK(
        id,
        '%22reset_invoicing_planning%22'
      ),
      { reset_invoicing_planning: true }
    )
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Executes a project action
 * @param orgPk - Organization ID
 * @param payload - Action data
 * @returns API response
 */
export const postProjectAction = (
  orgPk: number | string,
  payload: ProjectActionPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.PROJECTS.POST_PROJECT_ACTION(orgPk), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Tags
 **/

/**
 * Fetches project tags
 * @param orgPk - Organization ID
 * @returns Tags list
 */
export const fetchTags = (orgPk: number | string): Promise<any[]> => {
  return client
    .get(API_URL.PROJECTS.GET_PROJECT_TAGS_LIST(orgPk))
    .then(response => response.data.results)
    .catch(error => error.response)
}

/**
 * Creates a new tag
 * @param orgPk - Organization ID
 * @param payload - Tag data
 * @returns API response
 */
export const createTag = (
  orgPk: number | string,
  payload: ProjectTagPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.PROJECTS.POST_PROJECT_TAGS_LIST(orgPk), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates a tag
 * @param id - Tag ID
 * @param payload - Tag data
 * @returns API response
 */
export const updateTag = (
  id: number | string,
  payload: Partial<ProjectTagPayload>
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.PROJECTS.PATCH_PROJECTS_TAGS_PK(id), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Deletes a tag
 * @param id - Tag ID
 * @returns API response
 */
export const deleteTag = (id: number | string): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.PROJECTS.DELETE_PROJECTS_TAGS_PK(id))
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Groups
 **/

/**
 * Gets tag groups by organization
 * @param orgPk - Organization ID
 * @returns API response
 */
export const getGroupByOrg = (
  orgPk: number | string
): Promise<ApiClientResult> => {
  return client
    .get(API_URL.PROJECTS.GET_GROUP_BY_ORG(orgPk))
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Creates a tag group
 * @param orgId - Organization ID
 * @param payload - Tag group data
 * @returns API response
 */
export const createTagGroup = (
  orgId: number | string,
  payload: TagGroupPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.PROJECTS.POST_PROJECT_TAG_GROUPS_LIST(orgId), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates a tag group
 * @param id - Tag group ID
 * @param payload - Tag group data
 * @returns API response
 */
export const patchTagGroup = (
  id: number | string,
  payload: Partial<TagGroupPayload>
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.PROJECTS.PATCH_PROJECT_TAG_GROUP(id), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Deletes a tag group
 * @param id - Tag group ID
 * @returns API response
 */
export const deleteTagGroup = (
  id: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.PROJECTS.DELETE_PROJECT_TAG_GROUP(id))
    .then(response => response)
    .catch(error => error.response)
}

/* Projects bulk */

/**
 * Adds users to projects in bulk
 * @param orgPk - Organization ID
 * @param payload - Bulk data
 * @returns API response
 */
export const postProjectBulk = (
  orgPk: number | string,
  payload: ProjectBulkPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.PROJECTS.POST_PROJECTS_USER_BULK_ADD(orgPk), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Deletes users from projects in bulk
 * @param orgPk - Organization ID
 * @param payload - Bulk data
 * @returns API response
 */
export const deleteProjectBulk = (
  orgPk: number | string,
  payload: { data: ProjectBulkPayload }
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.PROJECTS.DELETE_PROJECTS_USER_BULK(orgPk), {
      data: payload
    })
    .then(response => response)
    .catch(error => error.response)
}

/* Project users */
/**
 * Executes a project users action
 * @param orgPk - Organization ID
 * @param teamPk - Team ID
 * @param payload - Action data
 * @returns API response
 */
export const postProjectUsersAction = (
  orgPk: number | string,
  teamPk: number | string,
  payload: any
): Promise<ApiClientResult> => {
  return client
    .post(
      API_URL.PROJECTS.POST_PROJECT_USERS_LIST_ACTION(orgPk, teamPk),
      payload
    )
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Adds a user to a project
 * @param data - Project user data
 * @returns API response
 */
export const addUser = (data: ProjectUserPayload): Promise<ApiClientResult> => {
  return client
    .post(API_URL.PROJECTS.POST_PROJECT_USERS, data)
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Deletes a user from a project
 * @param pk - Project user ID
 * @returns API response
 */
export const onDeleteOperationUser = (
  pk: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.PROJECTS.DELETE_PROJECT_USERS_PK(pk))
    .then(response => response)
    .catch(error => {
      return error.response
    })
}
